import type { EnvironmentType } from "../types"

export interface AnalyticsOptions {
  enabled: boolean
  url: string
  siteId: number
  userId?: string
  environment: EnvironmentType
  windowObj?: Window & typeof globalThis
}

/**
 * Analytics implementation that uses Matomo
 */
export class Analytics {
  private enabled: boolean
  private matomoUrl: string
  private siteId: number
  private environment: EnvironmentType
  private userId?: string
  private windowObj?: Window & typeof globalThis

  constructor(options: AnalyticsOptions) {
    this.enabled = options.enabled
    this.matomoUrl = options.url
    this.siteId = options.siteId
    this.environment = options.environment
    this.userId = options.userId
    this.windowObj = options.windowObj

    if (this.enabled && this.userId && this.windowObj) {
      this.setUserId(this.userId)
    }
  }

  /**
   * Creates the Matomo tracking script content
   * @returns The script content as a string
   */
  createTrackingScript(): string {
    return `
      var _paq = window._paq = window._paq || [];
      _paq.push(['setCustomDimension', 1, '${this.environment}']);
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
      (function() {
        var u="${this.matomoUrl}/";
        _paq.push(['setTrackerUrl', u+'matomo.php']);
        _paq.push(['setSiteId', ${this.siteId}]);
        var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
        g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
      })();
    `
  }

  trackPageView(title: string, url: string): void {
    if (!this.enabled || !this.windowObj) return

    const paq = this.getPaq()
    if (paq) {
      paq.push(["setDocumentTitle", title])
      paq.push(["setCustomUrl", url])
      paq.push(["trackPageView"])
    }
  }

  trackEvent(
    category: string,
    action: string,
    url: string,
    name?: string,
    value?: number,
  ): void {
    if (!this.enabled || !this.windowObj) return

    const paq = this.getPaq()
    if (paq) {
      const eventData = [category, action]
      if (name) eventData.push(name)
      if (value !== undefined) eventData.push(value.toString())

      paq.push(["setCustomUrl", url])
      paq.push(["trackEvent", ...eventData])
    }
  }

  setUserId(userId: string): void {
    if (!this.enabled || !this.windowObj) return

    const paq = this.getPaq()
    if (paq) {
      paq.push(["setUserId", userId])
    }
  }

  private getPaq(): any[] | undefined {
    return this.windowObj?._paq
  }
}

// Add the global _paq type
declare global {
  interface Window {
    _paq: any[]
  }
}
